import type { ExpandedSetupGuide } from '@/features/experiments/setup-guide/hook';
import { SetupGuideNavContainer } from './setup-guide-nav/container';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useTrackEvent } from '@/features/track/hook';
import { useSafeNavigate } from '@/context/safe-navigate';
import { SetupGuideCard } from './setup-guide-nav/card';
import { useStore } from '@nanostores/react';
import { activeRouteStore } from '@customerio/hydra-nav-state';

export enum SetupGuideStep {
  StartedAt = 'started_at',
  FirstMessageCreatedAt = 'first_message_created_at',
  SourcesCompleted = 'sources_completed',
  MessagingChannels = 'messaging_channels',
  FirstMessageSent = 'first_message_sent',
}

export type SetupGuideStatus = {
  isDone: boolean;
  isAtFirstStep: boolean;
  isBeyondFirstStep: boolean;
};

export interface SetupGuideProps {
  setupGuide: ExpandedSetupGuide;
}

export default function SetupGuide({ setupGuide }: SetupGuideProps) {
  const currentWorkspace = useCurrentWorkspace();
  const track = useTrackEvent();
  const safeNavigate = useSafeNavigate();

  // Temporary check while this is under experiment, this component should
  // be integrated into the main nav if the experiment is successful
  const activeRoute = useStore(activeRouteStore);
  const isActive = activeRoute.includes('/setup-guide');

  const workspaceCustomisedLink = () => {
    if (currentWorkspace == null) return '';
    return `/workspaces/${currentWorkspace?.id}/journeys/setup-guide/welcome`;
  };

  const handleNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
    track('Setup Guide Nav: Button Clicked');
    event.preventDefault();
    event.stopPropagation();
    safeNavigate(workspaceCustomisedLink());
  };

  // Progress is determined by 5 steps:
  // 1. started_at (number) - if setup guide has been started, always true            - 20%
  // 2. first_message_created_at (number)                                             - 40%
  // 3. required_people_added_at (number) or integration_completed_at (number) added  - 60%
  // 4. messaging_channels (array) - if messaging channels have been set up           - 80%
  // 5. first_message_sent (boolean) - if first message has been sent                 - 100%
  const {
    first_message_created_at,
    first_message_sent,
    integration_completed_at,
    messaging_channels,
    required_people_added_at,
  } = setupGuide ?? {};
  const steps = {
    [SetupGuideStep.StartedAt]: true,
    [SetupGuideStep.FirstMessageCreatedAt]: first_message_created_at != null,
    [SetupGuideStep.SourcesCompleted]: integration_completed_at != null || required_people_added_at != null,
    [SetupGuideStep.MessagingChannels]: (messaging_channels ?? []).every((channel) => channel.completed_at != null),
    [SetupGuideStep.FirstMessageSent]: first_message_sent === true,
  };

  const allStepsDone = Object.values(steps).filter(Boolean).length;
  const availableSteps = Object.keys(steps).length;
  const progress =
    (steps[SetupGuideStep.FirstMessageSent]
      ? availableSteps / availableSteps
      : allStepsDone / Object.keys(steps).length) * 100;

  return (
    <SetupGuideNavContainer
      link={workspaceCustomisedLink()}
      handleNavigation={handleNavigation}
      isClickable={true}
      isActive={isActive}
    >
      <SetupGuideCard progress={progress} />
    </SetupGuideNavContainer>
  );
}
